import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import OptionButton from '@/components/common/OptionButton/OptionButton';
import MinusIcon from '@/components/common/OptionButton/icons/Minus/MinusIcon';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import TerminateContractReasonModal from './components/TerminateContractReasonModal';
import useTerminateContractLines from './hooks/useTerminateContractLines';
import TerminateBulkUpdateModal from './terminateBulkUpdateModal/TerminateBulkUpdateModal';
import { CANCEL_TYPE } from '@/config/reasonTypes.config';
import { getReasonTypes } from '@/utils/data-helpers/reasonTypes';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import styles from './terminate-container.module.scss';
import {
  useSelectedLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { useSelectedVariableLines } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { BillingTypeEnum, ContractLineBillingCycleOutput, ContractStatusEnum }
  from '@/graphql/contracts/_generated_/contracts';

export default function TerminateStatusContainer({ readOnly }: { readOnly?: boolean }) {
  const { contractId }: { contractId: UUID } = useParams();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenBulkModal, setIsOpenBulkModal] = useState(false);
  const { terminateContractLines } = useTerminateContractLines();
  const reasonTypes = getReasonTypes();
  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const selectedLinesCount = selectedLineRows?.length > 0 ? selectedLineRows.length : selectedVariableLineRows?.length;
  const { cachedBCContractLines } =
    useGetContractLinesFromCache(contractId);

  const { contract } = useGetContractDetails(contractId);
  const { reasonCodes, isLoading, getReasonCodes } = useGetReasonCodes();

  const selectedTerminating = useMemo(() => {
    if (selectedLineRows.length > 0) {

      return cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items
        ?.filter((contractLine) => contractLine?.status == ContractStatusEnum.Terminating &&
          contractLine.billingType === BillingTypeEnum.Subscription &&
          selectedLineRows
            .map((selected) => selected.id)
            .includes(contractLine.id));
    }

    return [];
  }, [cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items,
    selectedLineRows
  ]) as unknown as ContractLineBillingCycleOutput[];

  const getLastBillingDate = useCallback(() => {
    const selectedIds = selectedLineRows?.map(({ id }) => id) ?? [];

    const items = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items;
    if (!items || selectedIds.length === 0) return null;

    const selectedItems = items.filter(
      (item): item is ContractLineBillingCycleOutput => item !== null && selectedIds.includes(item.id)
    );

    const dates = selectedItems
      .map((item) => item.lastBillingDate)
      .filter((d): d is string => d !== null && d !== undefined);

    if (dates.length === 0) return null;

    return dates.reduce((latest, current) =>
      new Date(current) > new Date(latest) ? current : latest
    );
  }, [cachedBCContractLines, selectedLineRows]);

  useEffect(() => {
    if (isOpenModal) {
      getReasonCodes(CANCEL_TYPE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  if (
    contract?.status.status === 'Cancelled' ||
    contract?.status.status === 'Expired' ||
    contract?.status.status === 'Inactive' ||
    contract?.status.status === 'Terminated') {
    return null;
  }

  if (readOnly && !(selectedTerminating?.length! > 0)) {
    return null;
  }

  return (
    <>
      {isOpenModal && (
        <TerminateContractReasonModal
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          contract={contract}
          terminateContractLines={terminateContractLines}
          onClose={() => setIsOpenModal(false)}
          selectedContractLinesCount={selectedLinesCount}
          isLoadingReasonCodes={isLoading}
          lastBillingDate={getLastBillingDate()}
          readOnly={readOnly}
        />
      )}

      {isOpenBulkModal && (
        <TerminateBulkUpdateModal
          terminateContractLines={selectedTerminating}
          onClose={() => setIsOpenBulkModal(false)}
          contract={contract!}
          readOnly={readOnly}
        />
      )
      }

      <div className={styles.checkboxWrapper}>
        <div>
          {selectedTerminating?.length! > 0 ? (
            <OptionButton
              Icon={<MinusIcon variant="terminate" />}
              onClick={() => setIsOpenBulkModal(true)}
            >
              Edit terminated line(s)
            </OptionButton>
          ) : !readOnly ? (
            <OptionButton
              Icon={<MinusIcon variant="terminate" />}
              onClick={() => setIsOpenModal(true)}
            >
              Terminate line(s)
            </OptionButton>
          ) : null}
        </div>
      </div>
    </>
  );
}
